import type { PostgrestClient } from '@supabase/postgrest-js';
import type { StorageClient } from '@supabase/storage-js';

import type { Database } from './db.types';

/**
 * Client returned by `getSupabaseClient()` (server) and
 * `useSupabaseClient()` (browser). Exposes `.from`, `.rpc`,
 * and `.schema` from `PostgrestClient` plus a lazy
 * `StorageClient` at `.storage`. Auth, realtime, and
 * functions are intentionally absent — Clerk owns auth and
 * the app has no live-data or edge-function paths.
 */
export type DbClient<T = Database> = PostgrestClient<T> & {
  readonly storage: StorageClient;
};

/**
 * Attaches a lazily-constructed `StorageClient` to a
 * `PostgrestClient` and returns the union. The cast is sound:
 * `Object.defineProperty` installs the getter at runtime; the
 * assertion only informs TypeScript of the widening.
 */
export function attachStorage<T>(
  pg: PostgrestClient<T>,
  factory: () => StorageClient
): DbClient<T> {
  let storage: StorageClient | null = null;
  Object.defineProperty(pg, 'storage', {
    enumerable: true,
    configurable: false,
    get(): StorageClient {
      if (!storage) storage = factory();
      return storage;
    },
  });
  return pg as PostgrestClient<T> & { readonly storage: StorageClient };
}
